import {ICashierExternalLinkHandler} from "./cashier.external-link-handler.interface";
import {OpenCashierOptions} from "../../../../../common/interfaces/cashier.service.interface";
import {
    ExternalLinkInIframeHandlerBase,
    ExternalLinkInIframeRendererParams
} from "../external-link-in-iframe-handler-base";
import {ExternalLinksZIndexes} from "../../external-links-z-indexes";
import {CIPropRead} from "../../../../../utils/case-insensitive-property-reader";
import {GNWP_SOURCES} from "../../../gnwp/gnwp-consts";
import {CyClass} from "../../../../../common/cy-classes";
import {
    AreaType,
    ControlCenterDeeplink,
    ControlCenterOpenSource,
    IChannel,
    IControlCenterFullSpecificParams
} from "@sparkware/uc-sdk-core";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";

export enum CASHIER_POST_ACTIONS {
    OPEN_CONTROL_CENTER = '101'
}

export class CashierExternalLinkHandler extends ExternalLinkInIframeHandlerBase implements ICashierExternalLinkHandler {

    protected _getGnwpSource(): GNWP_SOURCES {
        return GNWP_SOURCES.CASHIER;
    }

    private get _isOnHybrid(): boolean {
        return this.services.device.hybrid.inCasino || this.services.device.hybrid.isEmbedded;
    }

    private get _isOnMacSafari(): boolean {
        return this.services.device.os.isMacOS && this.services.device.browser.isSafari && !this.services.device.isProbablyIOSDevice;
    }

    private get _isMobileOS(): boolean {
        return this.services.device.os.isMobileOS || this.services.device.isProbablyIOSDevice;
    }

    async openCashier(options?: OpenCashierOptions): Promise<void> {
        if(!await this.services.user.ensureUserIsLoggedIn('Cashier')) {
            return;
        }

        const url = await this._getGnwpUrl(options);

        if(this._isOnHybrid) {
            this.services.hybrid.openExternalLink(url);
        } else if(this._isOnMacSafari) {
            this._openInNewTab(url);
        } else if(this._isMobileOS) {
            await this._redirect(url);
        } else {
            await this._showExternalLinkDialog(url);
        }
    }

    protected async _getGnwpUrl(options?: OpenCashierOptions): Promise<string> {
        return await this.services.gnwp.getCashierUrl({
            target: this._isMobileOS ? 1 : 2, //1 - redirect, 2 = iframe
            sessionType: options?.sessionType,
            promCode: options?.PromCode
        });
    }

    protected _getIframeRendererParams(): ExternalLinkInIframeRendererParams {
        return {
            maxWidth: 1017,
            maxHeight: 686,
            zIndex: ExternalLinksZIndexes.Cashier,
            hideCloseButton: false,
            httpVerb: 'POST',
            analyticsAction: this._getGnwpSource(),
            iframeExtraCssClass: CyClass('cy-cashier-external-link-container')
        };
    }

    protected async _onPostMessageReceived(message: MessageEvent): Promise<void> {

        if(CIPropRead.getStringProperty('state', this._parseMessageData(message.data))?.toLowerCase() === 'backtogame') {
            this._closeExternalLinkDialog();
            return;
        }

        const actionId = this._extractActionId(message);

        switch (actionId) {
            case CASHIER_POST_ACTIONS.OPEN_CONTROL_CENTER:
                this._openControlCenter(message);
                break;
            default:
                this.services.logger.debug('CashierExternalLinkHandler: Unknown actionId', actionId);
                break;
        }

    }

    private async _openControlCenter(message):Promise<void> {
        this._closeExternalLinkDialog();
        this.services.appLauncher.openControlCenterFull({
            businessCorrelationID: this.services.tools.getGuid(),
            channel: this._extractChannel(message),
            appSpecificParameters: this._extractAppSpecificParameters(message)
        });
    }

    private _extractChannel(message: MessageEvent): IChannel {
        const parsedChannelData = CIPropRead.getShallowLowercasedObject('channel', this._parseMessageData(message)) || {};
        const {area = AreaType.FullCashier, element = CHANNEL_ELEMENTS.cashierAction, source = CHANNEL_SOURCE.cashier} = parsedChannelData;
        return {
            area:area,
            element: element,
            source: source
        };
    }

    private _extractAppSpecificParameters(message: MessageEvent): IControlCenterFullSpecificParams {
        const parsedAppSpecificParamsData = CIPropRead.getShallowLowercasedObject('appSpecificParameters', this._parseMessageData(message)) || {};
        const {deeplink = ControlCenterDeeplink.Main, source = ControlCenterOpenSource.Cashier} = parsedAppSpecificParamsData;
        return {
            deeplink: deeplink,
            source: source
        };
    }
}